import { useRef } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Returns the pathname that corresponds to the currently *displayed* page.
 *
 * During navigation, `useLocation().pathname` updates as soon as the router
 * commits the new URL, but the route's loaders and Outlet can still be pending.
 * That causes layout (footer FAB, nav highlights, etc.) to reflect the new route
 * before the page content has rendered—icons change first, then the page.
 *
 * This hook defers the pathname until the new route has finished loading
 * (`isLoading` and `matches[].status === 'pending'` are false). While pending,
 * it returns the last resolved pathname so the layout stays in sync with the
 * visible content.
 */
export function useResolvedPathname(): string {
  const { pathname, isPending } = useRouterState({
    select: (s) => ({
      pathname: s.location.pathname,
      isPending:
        s.isLoading ||
        s.matches.some((d) => (d as { status?: string }).status === "pending"),
    }),
    structuralSharing: true,
  });

  const resolvedRef = useRef<string | null>(null);
  if (!isPending) {
    resolvedRef.current = pathname;
  }
  return isPending && resolvedRef.current != null
    ? resolvedRef.current
    : pathname;
}
